import { useRef, useState } from "react";
import hexToRgba from 'hex-to-rgba';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import Holder from "./Components/Holder";
import Navbar from "./Components/Navbar";
import Body from "./Components/Body";
import Notification from "./Components/Notification";
import Tiles from "./Components/Tiles";
import ListMetaRow from "./Components/ListLayouts/ListMetaRow";
import { LikeIcon, CommentIcon, LikeOutlineIcon, CommentOutlineIcon, CheckDoubleIcon, SearchGlyphIcon, BackChevronIcon, SearchLucideIcon } from "./Components/Icons";
import { COLORS, GRADIENTS } from "./theme";
import './App.css';

const DEVICE_PRESETS = [
  { key: 'iphone-6-9-display', label: 'iPhone 6.9" Display', dims: '1290x2796', shotWidth: 1290, shotHeight: 2796, deviceWidth: 946, tablet: false, android: false, marginOffset: '172px', extraTop: true, heroHeight: '760px' },
  { key: 'iphone-6-5-display', label: 'iPhone 6.5" Display', dims: '1284x2778', shotWidth: 1284, shotHeight: 2778, deviceWidth: 946, tablet: false, android: false, marginOffset: '148px', extraTop: false, heroHeight: '760px' },
  { key: 'ipad-13-display', label: 'iPad 13" Display', dims: '2064x2752', shotWidth: 2064, shotHeight: 2752, deviceWidth: 1846, tablet: true, android: false, marginOffset: '101px', extraTop: false, heroHeight: '1600px', heroMarginBottom: '60px' },
  { key: 'android-phone-display', label: 'Android Phone', dims: '1350x2400', shotWidth: 1350, shotHeight: 2400, deviceWidth: 880, tablet: false, android: true, marginOffset: '235px', extraTop: false, heroHeight: '820px' },
  { key: 'android-tablet-display', label: 'Android Tablet', dims: '1242x2208', shotWidth: 1242, shotHeight: 2208, deviceWidth: 1242, tablet: true, android: true, marginOffset: 'auto', extraTop: false, heroHeight: '1000px', heroMarginBottom: '44px' },
];

const NOTIFICATIONS_DISPLAY_TEXT = "Get instant updates and\nnotifications";
const UPDATES_DISPLAY_TEXT = "Be part of the conversation";
const SEARCH_DISPLAY_TEXT = "Search for and find\nthe information you need";

function HomeScreen({ preset, refIndex, holderRefs, app }) {
  const { themeColour, lightMode, appName, homeTitle, file, featuredTitle, gradients, showIcons, displayDates, listLayout } = app;
  return (
    <Holder ref={el => holderRefs.current[refIndex] = el} shotWidth={preset.shotWidth} shotHeight={preset.shotHeight} themeColour={themeColour} lightMode={lightMode} appName={appName} deviceWidth={preset.deviceWidth} tablet={preset.tablet} displayText={`Welcome to ${appName}`} imageName={`${preset.key}-1`} backgroundColor={'#F4F4F4'} marginOffset={preset.marginOffset}>
      <Navbar title={homeTitle} themeColour={themeColour} lightMode={lightMode} rightIcons={true} menuIcon={true} extraTop={preset.extraTop} />
      <Body>
        {listLayout !== 'socialList' && (
          <label htmlFor="featuredImage">
            <div className="featured-image" style={{ position: 'relative', marginBottom: preset.heroMarginBottom }}>
              <img src={file ? file : 'plus.png'} style={{ width: "100%", height: preset.heroHeight, objectFit: "cover", borderRadius: "16px" }} alt="" />
              <div style={{ position: "absolute", bottom: "2rem", left: "2rem", right: "2rem", zIndex: 20 }}>
                <p style={{ fontSize: "64px", fontWeight: "600", margin: 0, marginBottom: "0", zIndex: 20 }}>{featuredTitle}</p>
                <ListMetaRow showIcons={showIcons} displayDates={displayDates} date="1 hour" />
              </div>
              <div className='gradient' style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: GRADIENTS.imageOverlay, zIndex: 2, opacity: gradients ? "90%" : "0", borderRadius: "16px" }}>&nbsp;</div>
            </div>
          </label>
        )}
        <Tiles tablet={preset.tablet} listLayout={listLayout} gradients={gradients} showIcons={showIcons} displayDates={displayDates} themeColour={themeColour} tiles={app.tiles} />
      </Body>
    </Holder>
  );
}

function NotificationsScreen({ preset, refIndex, holderRefs, app }) {
  const { themeColour, lightMode, appName } = app;
  return (
    <Holder ref={el => holderRefs.current[refIndex] = el} shotWidth={preset.shotWidth} shotHeight={preset.shotHeight} themeColour={themeColour} lightMode={lightMode} appName={appName} deviceWidth={preset.deviceWidth} tablet={preset.tablet} backgroundColor="#F5F5F5" displayText={NOTIFICATIONS_DISPLAY_TEXT} imageName={`${preset.key}-2`} background="top" marginOffset={preset.marginOffset}>
      <Navbar title="Notifications" themeColour={themeColour} lightMode={lightMode} extraTop={preset.extraTop} />
      <Body>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: "1.75rem", color: lightMode ? "#000" : "#fff" }}>
          <CheckDoubleIcon size={36} />
          <p style={{ margin: 0, marginLeft: "8px", fontSize: "32px", fontWeight: "500" }}>Mark all as read</p>
        </div>
        <Notification text='Great feedback from ACME Corp!' timestamp="1 hour" unread={true} icon="bell.svg" quotes />
        <Notification text='Kate McGowan liked your comment: "Nice work!"' timestamp="4 hours" unread={true} icon="like-active.svg" image="person-3.png" />
        <Notification text='Some exciting news! ' timestamp="1 day" unread={false} icon="bell.svg" emoji={true} android={preset.android} />
        <Notification text='Ava Davis mentioned you in a comment: "Great to meet some new team members!"' timestamp="2 days" unread={false} icon="mentions.svg" image="person-4.png" />
      </Body>
    </Holder>
  );
}

function UpdatesScreen({ preset, refIndex, holderRefs, app }) {
  const { themeColour, lightMode, appName } = app;
  return (
    <Holder ref={el => holderRefs.current[refIndex] = el} shotWidth={preset.shotWidth} shotHeight={preset.shotHeight} themeColour={themeColour} lightMode={lightMode} appName={appName} deviceWidth={preset.deviceWidth} tablet={preset.tablet} displayText={UPDATES_DISPLAY_TEXT} imageName={`${preset.key}-3`} marginOffset={preset.marginOffset}>
      <Navbar title="Updates from the Team" themeColour={themeColour} lightMode={lightMode} compact={true} extraTop={preset.extraTop} />
      <Body compact={true} noPadding={true}>
        <p style={{ color: COLORS.textBody, fontSize: 36, fontWeight: 500, paddingLeft: 20, margin: 0, padding: "20px 40px 0 40px" }}>Congratulations to each and every one of you on a fantastic quarter. I am immensely proud to lead such a talented and dedicated team.</p>
        <p style={{ color: COLORS.textBody, fontSize: 36, fontWeight: 500, paddingLeft: 20, margin: 0, padding: "40px 40px 40px 40px" }}>Here's to our continued success!</p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 30, backgroundColor: "#fff", padding: "40px", borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ width: "280px" }}>
            <LikeIcon size={58} fill="#333336" style={{ marginRight: "1rem" }} />
            <CommentIcon size={58} fill={COLORS.textSecondary} />
          </div>
          <div style={{ display: "flex", width: "60%", textAlign: "right" }}>
            <p style={{ color: COLORS.textSecondary, fontSize: 36, fontWeight: 500, paddingLeft: 20, margin: 0, marginTop: 10, width: "100%", display: "block" }}>12 Likes · 1 Comment</p>
          </div>
        </div>
        <div style={{ display: "flex", backgroundColor: COLORS.surfaceMuted, padding: "40px", borderBottom: `1px solid ${COLORS.border}` }}>
          <img src="person-1.png" style={{ width: 100, height: 100, borderRadius: 60 }} alt="" />
          <div style={{ flexGrow: 1 }}>
            <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
              <p style={{ color: COLORS.textPrimary, fontSize: 36, fontWeight: 600, paddingLeft: 20, margin: 0 }}>Joseph Smith</p>
              <p style={{ color: COLORS.textSecondary, fontSize: 26, fontWeight: 500, paddingLeft: 10, margin: 0, width: "200px" }}>5 mins</p>
            </div>
            <p style={{ color: COLORS.textSecondary, fontSize: 26, fontWeight: 500, paddingLeft: 20, margin: 0, marginTop: 6 }}>Client Executive</p>
            <p style={{ color: COLORS.textPrimary, fontSize: 36, fontWeight: 500, paddingLeft: 20, margin: 0, marginTop: 16 }}>Great stuff everybody!</p>
            <div style={{ display: "flex" }}>
              <div style={{ display: "flex", alignItems: "center", paddingLeft: 20, marginTop: 30 }}>
                <LikeOutlineIcon size={30} />
                <p style={{ margin: 0, color: COLORS.textSecondary, fontSize: 34, fontWeight: 500, paddingLeft: 10 }}>Like</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", paddingLeft: 60, marginTop: 30 }}>
                <CommentOutlineIcon size={30} />
                <p style={{ margin: 0, color: COLORS.textSecondary, fontSize: 34, fontWeight: 500, paddingLeft: 10 }}>Comment</p>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", padding: "40px" }}>
          <img src="person-2.png" style={{ width: 100, height: 100, borderRadius: 60 }} alt="" />
          <p style={{ color: COLORS.textBody, fontSize: 36, fontWeight: 500, paddingLeft: 20, opacity: "60%", flexGrow: "1" }}>Write a comment</p>
        </div>
      </Body>
    </Holder>
  );
}

function SearchScreen({ preset, refIndex, holderRefs, app }) {
  const { themeColour, lightMode, appName } = app;

  const searchHeader = preset.android ? (
    <div className="header" style={{ backgroundColor: themeColour, width: "100%", padding: "38px 28px 28px 28px", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", color: lightMode ? "#000" : "#fff", paddingBottom: "28px" }}>
      <div style={{ display: "flex", flexGrow: 1, alignItems: "center", marginRight: 30, borderRadius: 12 }}>
        <BackChevronIcon size={64} style={{ margin: "0 20px 0" }} />
        <p style={{ fontSize: 42, fontWeight: 500, textAlign: "left", display: "flex", alignItems: "center", justifyContent: "center", color: lightMode ? "#000" : "#fff", margin: "30px 0px" }}>Search {appName}</p>
      </div>
      <div style={{ padding: "12px 1rem 0px" }}>
        <SearchGlyphIcon size={80} />
      </div>
    </div>
  ) : (
    <>
      <Navbar title="Search" themeColour={themeColour} lightMode={lightMode} compact={true} extraTop={preset.extraTop} />
      <div className="header" style={{ backgroundColor: themeColour, width: "100%", padding: "10px 28px 5px 28px", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", color: lightMode ? "#000" : "#fff", paddingBottom: "28px" }}>
        <div style={{ display: "flex", flexGrow: 1, alignItems: "center", background: "rgba(255,255,255,0.3)", marginRight: 30, borderRadius: 12 }}>
          <SearchLucideIcon size={48} style={{ padding: "0 20px 0" }} />
          <p style={{ fontSize: 42, fontWeight: 500, textAlign: "left", display: "flex", alignItems: "center", justifyContent: "center", color: lightMode ? "#000" : "#fff", margin: "30px 0px" }}>Search</p>
        </div>
        <div style={{ padding: "0 0.5rem 0" }}>
          <SearchGlyphIcon size={64} />
        </div>
      </div>
    </>
  );

  return (
    <Holder ref={el => holderRefs.current[refIndex] = el} shotWidth={preset.shotWidth} shotHeight={preset.shotHeight} themeColour={themeColour} lightMode={lightMode} appName={appName} deviceWidth={preset.deviceWidth} tablet={preset.tablet} displayText={SEARCH_DISPLAY_TEXT} imageName={`${preset.key}-4`} background="top" marginOffset={preset.marginOffset}>
      {searchHeader}
      <Body compact={true}>
        <img src="no_results.png" style={{ width: "240px", display: "block", margin: "300px auto 40px" }} alt="" />
        <p style={{ color: COLORS.textPrimary, opacity: "30%", fontSize: "36px", fontWeight: 500, textAlign: 'center' }}>Search for any content across {appName}</p>
      </Body>
    </Holder>
  );
}

function App() {

  const holderRefs = useRef([]);

  const [file, setFile] = useState();
  const [tiles, setTiles] = useState(() => Array.from({ length: 6 }, () => ({ image: undefined, title: '' })));

  const [featuredTitle, setFeaturedTitle] = useState('');

  const [listLayout, setListLayout] = useState('2columnList');

  const [themeColour, setThemeColour] = useState('#203554');
  const [appName, setAppName] = useState('App Name');
  const [homeTitle, setHomeTitle] = useState('Home List Title');
  const [lightMode, setLightMode] = useState(false);
  const [gradients, setGradients] = useState(false);
  const [displayDates, setDisplayDates] = useState(false);
  const [showIcons, setShowIcons] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleAllImages = async () => {
    try {
      setDownloadingAll(true);
      setExportError('');
      const zip = new JSZip();
      const results = await Promise.all(
        holderRefs.current.filter(Boolean).map(r => r.getImageDataUrl())
      );
      results.forEach(({ dataUrl, filename }) => {
        zip.file(filename, dataUrl.split(',')[1], { base64: true });
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${appName.replace(' ', '-').toLowerCase()}-app-store-screens.zip`);
      setDownloadingAll(false);
    } catch (err) {
      console.error('Error downloading all images:', err);
      setExportError('Unable to export screenshots. Try again or download an individual image.');
      setDownloadingAll(false);
    }
  };

  function handleFeaturedImageChange(e) {
    if (e.target.files[0]) setFile(URL.createObjectURL(e.target.files[0]));
  }

  function handleTileImageChange(index, e) {
    if (!e.target.files[0]) return;
    const url = URL.createObjectURL(e.target.files[0]);
    setTiles(prev => prev.map((tile, i) => i === index ? { ...tile, image: url } : tile));
  }

  function handleTileTitleChange(index, e) {
    const title = e.target.value;
    setTiles(prev => prev.map((tile, i) => i === index ? { ...tile, title } : tile));
  }

  function handleMultiImageChange(e) {
    if (e.target.files[0]) { setFile(URL.createObjectURL(e.target.files[0])) };
    setTiles(prev => prev.map((tile, i) =>
      e.target.files[i + 1] ? { ...tile, image: URL.createObjectURL(e.target.files[i + 1]) } : tile
    ));
  }

  function handleColorChange(e) {
    setThemeColour(e.target.value);

    let hex = hexToRgba(e.target.value);

    var rgb = hex.replace(/^(rgb|rgba)\(/, '').replace(/\)$/, '').replace(/\s/g, '').split(',');

    let r = rgb[0];
    let g = rgb[1];
    let b = rgb[2];

    let lum = (0.2126 * (r / 255)) + (0.7152 * (g / 255)) + (0.0722 * (b / 255));

    if ((lum) >= 0.673) {
      setLightMode(true);
    } else {
      setLightMode(false);
    }

  }

  function handleListLayoutChange(e) {
    setListLayout(e.target.value);
  }

  function handleAppNameChange(e) {
    setAppName(e.target.value);
  }

  function handleHomeTitleChange(e) {
    setHomeTitle(e.target.value);
  }

  function handleGradientChange() {
    setGradients(!gradients);
  }

  function handleDisplayDatesChange() {
    setDisplayDates(!displayDates);
  }

  function handleShowIconsChange() {
    setShowIcons(!showIcons);
  }

  function handleFeaturedTitleChange(e) {
    setFeaturedTitle(e.target.value);
  }

  const app = {
    themeColour, lightMode, appName, homeTitle, file, featuredTitle, gradients, showIcons, displayDates, listLayout, tiles,
  };

  return (
    <div className="studio">
      <a className="skip-link" href="#preview">Skip to previews</a>
      <header className="studio-header">
        <h1>App Store Screen Generator</h1>
        <button onClick={handleAllImages} className="export-button" disabled={downloadingAll}>
          {downloadingAll ? 'Exporting…' : 'Export all screenshots'}
        </button>
      </header>
      <div className="App">
        <aside className="AppControls" aria-label="Screenshot settings">
          <section className="control-section">
          <div>
            <label htmlFor="appName">App Name</label>
            <input id="appName" onChange={handleAppNameChange} value={appName} type='text' />
          </div>
          <div>
            <label htmlFor="themeColour">Theme Colour</label>
            <input type="color" id="themeColour" name="themeColour" value={themeColour} onChange={handleColorChange} />
          </div>
          <div>
            <label htmlFor="homeTitle">Home List Title Name</label>
            <input id="homeTitle" onChange={handleHomeTitleChange} value={homeTitle} type='text' />
          </div>

          </section>
          <section className="control-section">
          <div>
            <label htmlFor="listType">List Layout</label>
            <select name="listType" id="listType" value={listLayout} onChange={handleListLayoutChange}>
              <option value="socialList">Social List</option>
              <option value="smallList">Small List</option>
              <option value="mediumList">Medium List</option>
              <option value="2columnList">2 Column List</option>
              <option value="polaroid">Polaroid</option>
              <option value="miniPolaroid">Mini Polaroid</option>
              <option value="gallery">Gallery</option>
              <option value="letterbox">Letterbox</option>
              <option value="headline">Headline</option>
              <option value="feedMag">Feed Mag</option>
            </select>
          </div>

          <div style={{ display: "none" }}>
            <label htmlFor="featuredImage">Featured Image</label>
            <input type="file" id="featuredImage" onChange={handleFeaturedImageChange} />
          </div>
          <div className="inline">
            <label htmlFor="gradients">Show Gradients</label>
            <input id="gradients" type="checkbox" onChange={(e) => handleGradientChange(e.target.checked)} checked={gradients} />
          </div>
          <div className="inline">
            <label htmlFor="showIcons">Show Icons</label>
            <input id="showIcons" type="checkbox" onChange={(e) => handleShowIconsChange(e.target.checked)} checked={showIcons} />
          </div>
          <div className="inline">
            <label htmlFor="displayDates">Display Dates</label>
            <input id="displayDates" type="checkbox" onChange={(e) => handleDisplayDatesChange(e.target.checked)} checked={displayDates} />
          </div>
          </section>
          <section className="control-section">
          <div className="upload-control">
            <label htmlFor="multiImageUpload" className="imagesSelect">Select Featured Images</label>
            <input type="file" id="multiImageUpload" className="imagesSelectInput" accept="image/*" multiple onChange={handleMultiImageChange} />
          </div>
          <div>
            <label htmlFor="featuredTitle">Featured Title</label>
            <input id="featuredTitle" onChange={handleFeaturedTitleChange} value={featuredTitle} type='text' />
          </div>
          {tiles.map((tile, i) => (
            <div key={i}>
              <label htmlFor={`featuredTile${i + 1}Title`}>Tile {i + 1}</label>
              <input id={`featuredTile${i + 1}Title`} onChange={(e) => handleTileTitleChange(i, e)} value={tile.title} type='text' />
            </div>
          ))}
          {tiles.map((_, i) => (
            <div key={i} style={{ display: "none" }}>
              <label htmlFor={`listImage${i + 1}`}>Item {i + 1} Image</label>
              <input type="file" id={`listImage${i + 1}`} onChange={(e) => handleTileImageChange(i, e)} />
            </div>
          ))}
          </section>
        </aside>
        <main id="preview" className="preview-workspace">
          <p className="export-error" role="alert">{exportError}</p>
          {DEVICE_PRESETS.map((preset, breakpointIndex) => (
            <section key={preset.key} className="device-panel" aria-label={preset.label}>
              <div className="device-heading"><h2>{preset.label}</h2><span>{preset.dims.replace('x', ' × ')} px</span></div>
              <div className="AppScreens" tabIndex={0} aria-label={`${preset.label} screenshots`} >
                <HomeScreen preset={preset} refIndex={breakpointIndex * 4} holderRefs={holderRefs} app={app} />
                <NotificationsScreen preset={preset} refIndex={breakpointIndex * 4 + 1} holderRefs={holderRefs} app={app} />
                <UpdatesScreen preset={preset} refIndex={breakpointIndex * 4 + 2} holderRefs={holderRefs} app={app} />
                <SearchScreen preset={preset} refIndex={breakpointIndex * 4 + 3} holderRefs={holderRefs} app={app} />
              </div>

            </section>
          ))}

        </main>
      </div>
    </div>
  );
}

export default App;
